import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  Snackbar,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ComputerIcon from '@mui/icons-material/Computer';
import CloudIcon from '@mui/icons-material/Cloud';
import SaveIcon from '@mui/icons-material/Save';
import PrintingSettingsTab from '../../quickSale/settings/PrintingSettingsTab';
import salesService from '../../../../services/salesService';
import accountingService from '../../../../services/accountingService';
import {
  loadCashDrawerPrinter,
  saveCashDrawerPrinter,
} from '../../quickSale/utils/cashDrawerStorage';

interface DefinedPrinter {
  id: string;
  name: string;
}

interface InventoryCategory {
  id: string;
  name: string;
  parentId: string | null;
  level: number;
  createdAt: string;
  branchesId?: string | null;
}

interface LocalPrinterSettings {
  enabled: boolean;
  printingMode: 'single' | 'category';
  defaultReceiptPrinter: DefinedPrinter | null;
  categoryPrinterAssignments: { [categoryId: string]: string | null };
  // ✅ YENİ: Kategori başına 2. yazıcı (opsiyonel) — fiş her iki yazıcıya da gider
  categoryPrinterAssignments2?: { [categoryId: string]: string | null };
  adisyonPrinter: DefinedPrinter | null;
  uncategorizedPrinter: DefinedPrinter | null;
  definedPrinters: DefinedPrinter[];
  useBothFiscalAndReceiptPrinter: boolean;
  // ⚠️ cashDrawerPrinter bu objeye YAZILMAZ. Ayrı dedicated localStorage
  // anahtarında tutulur (bkz. `cashDrawerStorage.ts`).
}

interface LocalPrinterSettingsModalProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  quickSaleScreenId: number | null;
}

const LOCAL_PRINTER_SETTINGS_KEY_PREFIX = 'localPrinterSettings';

export function getLocalPrinterSettingsKey(companyId: string, screenId: string | number): string {
  return `${LOCAL_PRINTER_SETTINGS_KEY_PREFIX}_${companyId}_${screenId}`;
}

export function loadLocalPrinterSettings(companyId: string, screenId: string | number): LocalPrinterSettings | null {
  try {
    const key = getLocalPrinterSettingsKey(companyId, screenId);
    const json = localStorage.getItem(key);
    if (!json) return null;
    return JSON.parse(json) as LocalPrinterSettings;
  } catch {
    return null;
  }
}

const LocalPrinterSettingsModal: React.FC<LocalPrinterSettingsModalProps> = ({
  open,
  onClose,
  companyId,
  quickSaleScreenId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Local printer toggle
  const [useLocalPrinter, setUseLocalPrinter] = useState(false);

  // Printing settings state
  const [printingMode, setPrintingMode] = useState<'single' | 'category'>('category');
  const [defaultReceiptPrinter, setDefaultReceiptPrinter] = useState<DefinedPrinter | null>(null);
  const [definedPrinters, setDefinedPrinters] = useState<DefinedPrinter[]>([]);
  const [categoryPrinterAssignments, setCategoryPrinterAssignments] = useState<{ [categoryId: string]: string | null }>({});
  const [categoryPrinterAssignments2, setCategoryPrinterAssignments2] = useState<{ [categoryId: string]: string | null }>({});
  const [adisyonPrinter, setAdisyonPrinter] = useState<DefinedPrinter | null>(null);
  const [uncategorizedPrinter, setUncategorizedPrinter] = useState<DefinedPrinter | null>(null);
  const [useBothFiscalAndReceiptPrinter, setUseBothFiscalAndReceiptPrinter] = useState(false);
  const [cashDrawerPrinter, setCashDrawerPrinter] = useState<DefinedPrinter | null>(null);
  const [cashDrawerTestLoading, setCashDrawerTestLoading] = useState(false);

  // Categories for printing tab
  const [allCategories, setAllCategories] = useState<InventoryCategory[]>([]);
  const [selectedCategoriesPerWarehouse, setSelectedCategoriesPerWarehouse] = useState<{ [warehouseId: string]: string[] }>({});

  // Local printers from local-service
  const [localPrinters, setLocalPrinters] = useState<DefinedPrinter[]>([]);
  const [loadingLocalPrinters, setLoadingLocalPrinters] = useState(false);
  const [localPrintersError, setLocalPrintersError] = useState<string | null>(null);
  const [printTestLoading, setPrintTestLoading] = useState<string | null>(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const uniqueCategoryIds = useMemo(() => {
    const allSelectedCatIds = Object.values(selectedCategoriesPerWarehouse).flat();
    return Array.from(new Set(allSelectedCatIds));
  }, [selectedCategoriesPerWarehouse]);

  const categoriesToListForPrinting = useMemo(() => {
    return uniqueCategoryIds
      .map(catId => allCategories.find(c => c.id === catId))
      .filter((c): c is InventoryCategory => !!c)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [uniqueCategoryIds, allCategories]);

  const fetchLocalPrinters = useCallback(async () => {
    setLoadingLocalPrinters(true);
    setLocalPrintersError(null);
    try {
      const response = await fetch('http://localhost:3003/api/yazicilariGetir');
      if (!response.ok) {
        throw new Error(`Yerel yazıcı servisine ulaşılamadı (Kod: ${response.status})`);
      }
      const printersFromService: Array<{ id: string; name: string }> = await response.json();
      setLocalPrinters(printersFromService.map(p => ({ id: p.id, name: p.name })));
    } catch (err: any) {
      setLocalPrintersError(err.message || 'Yerel yazıcılar alınamadı.');
      setLocalPrinters([]);
    } finally {
      setLoadingLocalPrinters(false);
    }
  }, []);

  const handlePrintTestPage = useCallback(async (printerId: string, printerName: string) => {
    setPrintTestLoading(printerId);
    try {
      await fetch('http://localhost:3003/api/sinamaSayfasiYazdir', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ printerName: printerId }),
      });
    } catch (err: any) {
      console.error('Test sayfası yazdırma hatası:', err);
    } finally {
      setPrintTestLoading(null);
    }
  }, []);

  const handleTestCashDrawer = useCallback(async (printerName: string) => {
    setCashDrawerTestLoading(true);
    try {
      const response = await fetch('http://localhost:3003/api/print/open-cash-drawer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ printerName }),
      });
      const data = await response.json().catch(() => ({ success: response.ok }));
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || `HTTP ${response.status}`);
      }
      setSnackbar({ open: true, message: 'Para çekmecesi tetiği gönderildi.', severity: 'success' });
    } catch (err: any) {
      console.error('Para çekmecesi test hatası:', err);
      setSnackbar({ open: true, message: 'Çekmece tetiği başarısız: ' + (err.message || 'Bilinmeyen hata'), severity: 'error' });
    } finally {
      setCashDrawerTestLoading(false);
    }
  }, []);

  // Load data when modal opens
  useEffect(() => {
    if (!open || !companyId || !quickSaleScreenId) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load screen data for StockVisibilityJson (categories) and current DB printer settings
        const [screenData, categoriesData] = await Promise.all([
          salesService.getQuickSaleScreenById(quickSaleScreenId),
          accountingService.getCategories(),
        ]);

        // Parse StockVisibilityJson to get selected categories
        if (screenData.StockVisibilityJson) {
          try {
            const stockVisibility = JSON.parse(screenData.StockVisibilityJson);
            setSelectedCategoriesPerWarehouse(stockVisibility.categories || {});
          } catch (e) {
            console.error('StockVisibilityJson parse hatası:', e);
          }
        }

        // Load all categories
        if (Array.isArray(categoriesData)) {
          const categoryList: InventoryCategory[] = categoriesData
            .map((c: any) => ({
              id: c.Id?.toString() || c.id?.toString() || '',
              name: c.Name || c.name || 'İsimsiz Kategori',
              parentId: c.ParentId?.toString() || c.parentId?.toString() || null,
              level: c.Level || c.level || 1,
              createdAt: c.CreatedAt || c.createdAt || '',
              branchesId: null,
            }))
            .filter(c => c.id)
            .sort((a, b) => a.name.localeCompare(b.name));
          setAllCategories(categoryList);
        }

        // Check localStorage for existing local settings
        const key = getLocalPrinterSettingsKey(companyId, quickSaleScreenId);
        const savedJson = localStorage.getItem(key);

        if (savedJson) {
          try {
            const saved: LocalPrinterSettings = JSON.parse(savedJson);
            setUseLocalPrinter(saved.enabled);
            setPrintingMode(saved.printingMode || 'category');
            setDefaultReceiptPrinter(saved.defaultReceiptPrinter || null);
            setDefinedPrinters(saved.definedPrinters || []);
            setCategoryPrinterAssignments(saved.categoryPrinterAssignments || {});
            setCategoryPrinterAssignments2(saved.categoryPrinterAssignments2 || {});
            setAdisyonPrinter(saved.adisyonPrinter || null);
            setUncategorizedPrinter(saved.uncategorizedPrinter || null);
            setUseBothFiscalAndReceiptPrinter(saved.useBothFiscalAndReceiptPrinter || false);
          } catch (e) {
            console.error('localStorage local printer settings parse hatası:', e);
          }
        } else {
          // No local settings yet - initialize from DB settings as defaults
          if (screenData.PrintingSettingsJson) {
            try {
              const dbSettings = JSON.parse(screenData.PrintingSettingsJson);
              setPrintingMode(dbSettings.printingMode || 'category');
              setDefaultReceiptPrinter(dbSettings.defaultReceiptPrinter || null);
              setDefinedPrinters(dbSettings.definedPrinters || []);
              setCategoryPrinterAssignments(dbSettings.categoryPrinterAssignments || {});
              setCategoryPrinterAssignments2(dbSettings.categoryPrinterAssignments2 || {});
              setAdisyonPrinter(dbSettings.adisyonPrinter || null);
              setUncategorizedPrinter(dbSettings.uncategorizedPrinter || null);
              setUseBothFiscalAndReceiptPrinter(dbSettings.useBothFiscalAndReceiptPrinter || false);
            } catch (e) {
              console.error('PrintingSettingsJson parse hatası:', e);
            }
          }
          setUseLocalPrinter(false);
        }

        // 💰 Para çekmecesi tetik yazıcısı → ayrı dedicated localStorage anahtarı
        const savedCashDrawer = loadCashDrawerPrinter(companyId, quickSaleScreenId);
        setCashDrawerPrinter(savedCashDrawer);

        // Fetch local printers
        await fetchLocalPrinters();

      } catch (err: any) {
        console.error('Local printer settings veri yükleme hatası:', err);
        setError(err.message || 'Veriler yüklenirken hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [open, companyId, quickSaleScreenId, fetchLocalPrinters]);

  const handleSave = useCallback(() => {
    if (!companyId || !quickSaleScreenId) return;

    const settings: LocalPrinterSettings = {
      enabled: useLocalPrinter,
      printingMode,
      defaultReceiptPrinter,
      categoryPrinterAssignments,
      categoryPrinterAssignments2,
      adisyonPrinter,
      uncategorizedPrinter,
      definedPrinters,
      useBothFiscalAndReceiptPrinter,
    };

    try {
      const key = getLocalPrinterSettingsKey(companyId, quickSaleScreenId);
      localStorage.setItem(key, JSON.stringify(settings));
      // 💰 Para çekmecesi tetik yazıcısı → ayrı dedicated localStorage anahtarı
      saveCashDrawerPrinter(companyId, quickSaleScreenId, cashDrawerPrinter);
      setSnackbar({ open: true, message: 'Yazıcı ayarları bu bilgisayara kaydedildi.', severity: 'success' });
      setTimeout(() => onClose(), 500);
    } catch (err: any) {
      setSnackbar({ open: true, message: 'Kayıt hatası: ' + (err.message || 'Bilinmeyen hata'), severity: 'error' });
    }
  }, [companyId, quickSaleScreenId, useLocalPrinter, printingMode, defaultReceiptPrinter, categoryPrinterAssignments, categoryPrinterAssignments2, adisyonPrinter, uncategorizedPrinter, definedPrinters, useBothFiscalAndReceiptPrinter, cashDrawerPrinter, onClose]);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { minHeight: '60vh', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <PrintIcon color="primary" />
          <Typography variant="h6" component="span">
            Yazıcı Ayarları (Bu Bilgisayar)
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Yükleniyor...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
          ) : (
            <>
              {/* Local Yazıcı Kullan Toggle */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2,
                  border: '2px solid',
                  borderColor: useLocalPrinter ? 'success.main' : 'divider',
                  bgcolor: useLocalPrinter ? 'success.lighter' : 'background.default',
                  borderRadius: 2,
                  transition: 'all 0.3s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ComputerIcon sx={{ color: useLocalPrinter ? 'success.main' : 'text.secondary', fontSize: 28 }} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        Bu Bilgisayarın Yazıcılarını Kullan
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Aktif edildiğinde, ana ekran ayarları yerine bu bilgisayara özel yazıcı ayarları kullanılır.
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useLocalPrinter}
                        onChange={(e) => setUseLocalPrinter(e.target.checked)}
                        color="success"
                        size="medium"
                      />
                    }
                    label={useLocalPrinter ? 'Aktif' : 'Kapalı'}
                    labelPlacement="start"
                  />
                </Box>
              </Paper>

              {/* Toggle OFF message */}
              {!useLocalPrinter && (
                <Alert severity="info" icon={<CloudIcon />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Ana ekran ayarları kullanılıyor.</strong> Bu bilgisayar, veritabanındaki merkezi yazıcı
                    ayarlarını kullanacaktır. Farklı yazıcı kullanmak istiyorsanız yukarıdaki ayarı aktif edin.
                  </Typography>
                </Alert>
              )}

              {/* Toggle ON - show PrintingSettingsTab */}
              {useLocalPrinter && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <PrintingSettingsTab
                    printingMode={printingMode}
                    defaultReceiptPrinter={defaultReceiptPrinter}
                    definedPrinters={definedPrinters}
                    categoryPrinterAssignments={categoryPrinterAssignments}
                    categoryPrinterAssignments2={categoryPrinterAssignments2}
                    adisyonPrinter={adisyonPrinter}
                    onAdisyonPrinterChange={setAdisyonPrinter}
                    uncategorizedPrinter={uncategorizedPrinter}
                    onUncategorizedPrinterChange={setUncategorizedPrinter}
                    useBothFiscalAndReceiptPrinter={useBothFiscalAndReceiptPrinter}
                    onUseBothFiscalAndReceiptPrinterChange={setUseBothFiscalAndReceiptPrinter}
                    isRestaurantMode={true}
                    uniqueCategoryIds={uniqueCategoryIds}
                    categoriesToListForPrinting={categoriesToListForPrinting}
                    localPrinters={localPrinters}
                    loadingLocalPrinters={loadingLocalPrinters}
                    localPrintersError={localPrintersError}
                    onPrintingModeChange={(_e, newMode) => setPrintingMode(newMode as 'single' | 'category')}
                    onSetDefaultPrinter={(printer) => setDefaultReceiptPrinter(printer)}
                    onRemoveDefaultPrinter={() => setDefaultReceiptPrinter(null)}
                    onCategoryPrinterChange={(categoryId, printerId) => {
                      setCategoryPrinterAssignments(prev => ({ ...prev, [categoryId]: printerId }));
                    }}
                    onCategoryPrinterChange2={(categoryId, printerId) => {
                      setCategoryPrinterAssignments2(prev => ({ ...prev, [categoryId]: printerId }));
                    }}
                    onRefreshLocalPrinters={fetchLocalPrinters}
                    onPrintTestPage={handlePrintTestPage}
                    printTestLoading={printTestLoading}
                    onOpenReceiptSettingsModal={() => {}}
                    receiptNumberEnabled={false}
                    cashDrawerPrinter={cashDrawerPrinter}
                    onCashDrawerPrinterChange={setCashDrawerPrinter}
                    onTestCashDrawer={handleTestCashDrawer}
                    cashDrawerTestLoading={cashDrawerTestLoading}
                  />
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit">
            İptal
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loading}
          >
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        message={snackbar.message}
      />
    </>
  );
};

export default LocalPrinterSettingsModal;
